import {
    AggregatorObject,
    AllowedPrivacy,
    BaseSchedule,
    BaseScheduleType,
    GreaterThanFilter,
    InFilter,
    InScheduleFilter,
    LesserThanFilter,
    MembershipUsageType,
    OrFilter,
    PurchaseRule,
    PurchaseRuleFilterObject,
    RuleFilterItem,
    SelectionState,
} from "types";
import {
    membershipRestrictionFields,
    MembershipUsageLimit,
} from "@/components/Product/Membership/MembershipRestrictions/MembershipRestrictions";

export const RedemptionRuleTransformer = {
    getExperiences: (rule: PurchaseRule): SelectionState | null => {
        const experiencesFilter = rule.filter?.filters?.find(
            (filter) => filter.object === PurchaseRuleFilterObject.IN_FILTER && filter.field === "product.id",
        ) as InFilter | undefined;

        if (!experiencesFilter) return null;

        /** When experience filter exists in redemption purchase rule, means user has selected specific "products" and not all experiences */
        return { all: false, selected: experiencesFilter.operand as ID[] };
    },

    getAllowedPrivacies: (rule: PurchaseRule): AllowedPrivacy[] | null => {
        const privacyFilter = rule.filter?.filters?.find(
            (filter) => filter.object === PurchaseRuleFilterObject.IN_FILTER && filter.field === "privacy",
        ) as InFilter | undefined;

        if (!privacyFilter) return null;

        return privacyFilter.operand as AllowedPrivacy[];
    },

    isOrganizerRequired: (rule: PurchaseRule): boolean => {
        return !!rule.filter?.filters?.some(
            (filter) =>
                filter.object === PurchaseRuleFilterObject.EQUALS_FILTER &&
                filter.field === membershipRestrictionFields.organizer,
        );
    },

    getExpiry: (rule: PurchaseRule): number | null => {
        const expiryFilter = rule.filter?.filters?.find((filter) => {
            if (filter.object !== PurchaseRuleFilterObject.LESSER_THAN_FILTER) return false;

            const { aggregator } = filter;
            if (!aggregator || aggregator.object !== AggregatorObject.DIFFERENCE_AGGREGATOR) {
                return false;
            }

            return aggregator.operands?.some(
                (aggregatorItem) => aggregatorItem.operand === "membershipPurchaseItem.createdAt.timestamp",
            );
        }) as GreaterThanFilter | undefined;

        if (!expiryFilter) return null;

        /** `filter.operand` would be in milliseconds. We need to convert that to minutes */
        return expiryFilter.operand / 60;
    },

    getSchedules: (purchaseRule: PurchaseRule, type: BaseScheduleType, field: string): BaseSchedule[] => {
        if (!purchaseRule) {
            return [];
        }

        if (purchaseRule.filter?.object !== "and_filter") {
            return [];
        }

        const inScheduleFilters = purchaseRule.filter?.filters.filter((filter) => {
            return filter.object === "in_schedule_filter";
        }) as InScheduleFilter[];

        const filter = inScheduleFilters.find((filter) => {
            return filter.schedules.some((schedule) => {
                return schedule.type === type && filter.field === field;
            });
        });

        return filter?.schedules ?? [];
    },

    getUsage: (rule: PurchaseRule): MembershipUsageLimit => {
        const defaultLimit: MembershipUsageLimit = {
            usageType: MembershipUsageType.UNLIMITED,
            usageLimit: 0,
        };

        const isUsageFilter = (filter: RuleFilterItem): filter is LesserThanFilter =>
            filter.object === PurchaseRuleFilterObject.LESSER_THAN_FILTER &&
            filter.field === "appliedMembershipCouponCode.uses";

        const usageOrFilter = rule.filter?.filters?.find((filter) => {
            return (
                filter.object === PurchaseRuleFilterObject.OR_FILTER &&
                filter.filters?.some((filterItem) => isUsageFilter(filterItem))
            );
        }) as OrFilter | undefined;

        if (!usageOrFilter) return defaultLimit;

        const usageFilter = usageOrFilter.filters.find((filterItem) => isUsageFilter(filterItem))! as LesserThanFilter;

        return {
            usageType: MembershipUsageType.LIMITED,
            usageLimit: Number(usageFilter.operand),
        };
    },
};
